import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { lazy, Suspense, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Brand } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError, errorMessage } from "@/lib/api";
import type { Me } from "@/lib/session";
import { NOTICE_KEY } from "@/lib/sign-in-notice";

function AuthShell({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-linear-to-b/srgb from-secondary/50 to-background px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <Brand />
        </div>
        <div className="rounded-2xl border border-card-border bg-card p-8 shadow-xs">
          <h1 className="text-2xl text-foreground/90">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
        <p className="mt-6 text-center text-xs text-muted-foreground">Your information is encrypted and protected by two-step verification.</p>
      </div>
    </main>
  );
}

function FormError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
      {errorMessage(error)}
    </p>
  );
}

function useRefreshMe() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["/api/auth/me"] });
}

const ClerkSignIn = lazy(() => import("./clerk-sign-in"));

/** Public sign-in settings. `passwordResetEmail` says how this server sends "Forgot your password?" links. */
type AuthConfig = { clerkPublishableKey: string | null; passwordResetEmail?: "email" | "development_log" | "off" };

function readNotice(): string | null {
  try {
    return sessionStorage.getItem(NOTICE_KEY);
  } catch {
    return null;
  }
}

export function LoginPage() {
  const config = useQuery<AuthConfig>({ queryKey: ["/api/auth/config"], staleTime: Infinity });
  const [notice] = useState(readNotice);
  // Show it once: cleared after it's on screen (not while reading, which React may repeat).
  useEffect(() => {
    try {
      sessionStorage.removeItem(NOTICE_KEY);
    } catch {
      /* storage blocked */
    }
  }, []);
  const [usePassword, setUsePassword] = useState(false);
  const clerkKey = config.data?.clerkPublishableKey ?? null;
  const showClerk = !!clerkKey && !usePassword;

  return (
    <AuthShell title="Sign in" subtitle="Sign in to your ClientFlow account.">
      {notice && (
        <p role="alert" className="mb-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {notice}
        </p>
      )}
      {config.isPending ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
      ) : showClerk ? (
        <>
          <Suspense fallback={<p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>}>
            <ClerkSignIn publishableKey={clerkKey} />
          </Suspense>
          <button type="button" className="mt-4 w-full text-center text-sm font-medium text-primary hover:underline" onClick={() => setUsePassword(true)}>
            Sign in with a ClientFlow password instead
          </button>
        </>
      ) : (
        <>
          <PasswordForm />
          {clerkKey && (
            <button type="button" className="mt-4 w-full text-center text-sm font-medium text-primary hover:underline" onClick={() => setUsePassword(false)}>
              Sign in with Google or an email code instead
            </button>
          )}
        </>
      )}
      <p className="mt-5 text-center text-sm text-muted-foreground">
        New to ClientFlow?{" "}
        <a href="/" className="font-medium text-primary hover:underline">
          Learn more
        </a>
      </p>
    </AuthShell>
  );
}

function PasswordForm() {
  const refresh = useRefreshMe();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const login = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", "/api/auth/login", { email, password }),
    onSuccess: refresh,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="email">Email</Label>
        <Input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between gap-3">
          <Label htmlFor="password">Password</Label>
          <a href="/forgot-password" className="text-sm font-medium text-primary hover:underline">
            Forgot your password?
          </a>
        </div>
        <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
      </div>
      <FormError error={login.error} />
      <Button type="submit" className="w-full" disabled={login.isPending}>
        {login.isPending ? "Signing in…" : "Sign in"}
      </Button>
    </form>
  );
}

export function MfaVerifyPage() {
  const refresh = useRefreshMe();
  const [code, setCode] = useState("");
  const verify = useMutation({
    meta: { inlineError: true }, mutationFn: () => api.send("POST", "/api/auth/mfa/verify", { code }), onSuccess: refresh });
  const logout = useMutation({ mutationFn: () => api.send("POST", "/api/auth/logout"), onSuccess: () => window.location.assign("/sign-in") });
  return (
    <AuthShell title="Two-step verification" subtitle="Enter the 6-digit code from your authenticator app.">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          verify.mutate();
        }}
        className="space-y-4"
      >
        <Input
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d{6}"
          maxLength={6}
          autoFocus
          aria-label="6-digit code"
          className="text-center text-2xl tracking-[0.5em]"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        />
        <FormError error={verify.error} />
        <Button type="submit" className="w-full" disabled={code.length !== 6 || verify.isPending}>
          Verify
        </Button>
        <Button type="button" variant="ghost" className="w-full" onClick={() => logout.mutate()}>
          Use a different account
        </Button>
      </form>
    </AuthShell>
  );
}

/** First-time MFA enrollment (required for staff). */
export function MfaSetup({ onDone, embedded }: { onDone?: () => void; embedded?: boolean }) {
  const refresh = useRefreshMe();
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [qr, setQr] = useState<string>("");
  const [code, setCode] = useState("");
  const start = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ secret: string; otpauthUrl: string }>("POST", "/api/auth/mfa/setup"),
    onSuccess: (d) => setSetup(d),
  });
  const enable = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", "/api/auth/mfa/enable", { code }),
    onSuccess: async () => {
      await refresh();
      onDone?.();
    },
  });
  useEffect(() => {
    if (setup) QRCode.toDataURL(setup.otpauthUrl, { margin: 1, width: 200 }).then(setQr);
  }, [setup]);

  const body = !setup ? (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        You'll need an authenticator app such as Google Authenticator, Microsoft Authenticator, 1Password or Authy. It protects client
        information even if a password is stolen.
      </p>
      <FormError error={start.error} />
      <Button className="w-full" onClick={() => start.mutate()} disabled={start.isPending}>
        Set up two-step verification
      </Button>
    </div>
  ) : (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        enable.mutate();
      }}
    >
      <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
        <li>Open your authenticator app and scan this code.</li>
        <li>Enter the 6-digit code it shows.</li>
      </ol>
      {qr && <img src={qr} alt="QR code for your authenticator app" className="mx-auto h-48 w-48 rounded-lg border" />}
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer">Unable to scan? Enter this key manually</summary>
        <code className="mt-2 block break-all rounded bg-muted p-2 text-foreground">{setup.secret}</code>
      </details>
      <Input
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        aria-label="6-digit code"
        className="text-center text-2xl tracking-[0.5em]"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
      />
      <FormError error={enable.error} />
      <Button type="submit" className="w-full" disabled={code.length !== 6 || enable.isPending}>
        Turn on
      </Button>
    </form>
  );
  if (embedded) return body;
  return (
    <AuthShell title="Set up two-step verification" subtitle="Two-step verification is required for all firm staff.">
      {body}
    </AuthShell>
  );
}

/** Invitations from firms, accepted from inside the person's own account. */
export function Invitations({ me }: { me: Me }) {
  const refresh = useRefreshMe();
  const act = useMutation({
    mutationFn: (v: { id: string; action: "accept" | "decline" }) => api.send("POST", `/api/auth/invitations/${v.id}/${v.action}`),
    onSuccess: refresh,
  });
  if (!me.invitations?.length) return null;
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Invitations</p>
      {me.invitations.map((inv) => (
        <div key={inv.membershipId} className="flex items-center gap-2 rounded-lg border border-accent/60 bg-accent/10 px-3 py-2">
          <span className="flex-1 text-sm">
            <span className="font-medium">{inv.firmName}</span> invited you {inv.role === "client" ? "to their client portal" : `as ${inv.role}`}
          </span>
          <Button size="sm" onClick={() => act.mutate({ id: inv.membershipId, action: "accept" })} disabled={act.isPending}>
            Accept
          </Button>
          <Button size="sm" variant="ghost" onClick={() => act.mutate({ id: inv.membershipId, action: "decline" })} disabled={act.isPending}>
            Decline
          </Button>
        </div>
      ))}
    </div>
  );
}

export function FirmPickerPage({ me }: { me: Me }) {
  const refresh = useRefreshMe();
  const pick = useMutation({
    meta: { inlineError: true }, mutationFn: (firmId: string) => api.send("POST", "/api/auth/switch-firm", { firmId }), onSuccess: refresh });
  return (
    <AuthShell title="Select an account" subtitle="You have access to more than one firm.">
      <div className="space-y-2">
        {me.memberships?.map((m) => (
          <button
            key={m.firmId}
            onClick={() => pick.mutate(m.firmId)}
            className="flex w-full items-center justify-between rounded-lg border border-border px-4 py-3 text-left hover:bg-muted"
          >
            <span className="font-medium">{m.firmName}</span>
            <span className="text-xs capitalize text-muted-foreground">{m.role === "client" ? "Client portal" : m.role}</span>
          </button>
        ))}
        <FormError error={pick.error} />
        <Invitations me={me} />
      </div>
    </AuthShell>
  );
}

export function NoAccessPage({ me }: { me: Me }) {
  const refresh = useRefreshMe();
  const logout = useMutation({ mutationFn: () => api.send("POST", "/api/auth/logout"), onSuccess: () => window.location.assign("/sign-in") });
  const hasInvites = !!me.invitations?.length;
  return (
    <AuthShell
      title={hasInvites ? "Pending invitation" : "No access"}
      subtitle={hasInvites ? "Accept an invitation to continue." : "Your account is not linked to a firm. Please ask your firm to send you an invitation."}
    >
      <div className="mb-4">
        <Invitations me={me} />
      </div>
      <Button variant="outline" className="w-full" onClick={() => logout.mutate()}>
        Sign out
      </Button>
    </AuthShell>
  );
}

export function AcceptInvitePage() {
  const token = new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "";
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const clerkKey = useQuery<AuthConfig>({ queryKey: ["/api/auth/config"], staleTime: Infinity }).data?.clerkPublishableKey;
  const accept = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", "/api/auth/accept-invite", { token, name, password }),
    onSuccess: () => {
      // Remove the token from the address bar and history.
      window.history.replaceState(null, "", "/sign-in");
      setDone(true);
    },
  });
  if (done) {
    return (
      <AuthShell title="Account created" subtitle="Your account is ready to use.">
        <Button className="w-full" onClick={() => window.location.assign("/sign-in")}>
          Sign in
        </Button>
      </AuthShell>
    );
  }
  const mismatch = confirm.length > 0 && confirm !== password;
  return (
    <AuthShell title="Create your account" subtitle="Choose a password of at least 12 characters, including a letter and a number.">
      {clerkKey && (
        <div className="mb-6 border-b border-border pb-6">
          <Button variant="outline" className="w-full" onClick={() => window.location.assign("/sign-in")}>
            Continue with Google or an email code
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">Use the email address this invitation was sent to; no password is required. Alternatively, set a password below.</p>
        </div>
      )}
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!mismatch) accept.mutate();
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="name">Your name</Label>
          <Input id="name" required autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pw">Password</Label>
          <Input id="pw" type="password" autoComplete="new-password" minLength={12} required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pw2">Confirm password</Label>
          <Input id="pw2" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          {mismatch && <p className="text-xs text-destructive">Passwords do not match</p>}
        </div>
        <FormError error={accept.error} />
        <Button type="submit" className="w-full" disabled={!token || accept.isPending || mismatch}>
          Create account
        </Button>
        {!token && <p className="text-sm text-destructive">This invitation link is incomplete. Please open it directly from the invitation email.</p>}
      </form>
    </AuthShell>
  );
}

function BackToSignIn() {
  return (
    <p className="mt-5 text-center text-sm text-muted-foreground">
      <a href="/sign-in" className="font-medium text-primary hover:underline">
        Back to sign in
      </a>
    </p>
  );
}

/**
 * "Forgot your password?": always the same answer, whether or not the address has an account. When this
 * server can't send email, says so instead of offering a link that would never arrive.
 */
export function ForgotPasswordPage() {
  const config = useQuery<AuthConfig>({ queryKey: ["/api/auth/config"], staleTime: Infinity });
  const [email, setEmail] = useState("");
  const send = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ ok: true; message: string }>("POST", "/api/auth/forgot-password", { email }),
  });
  const mode = config.data?.passwordResetEmail ?? "email";
  if (config.isPending) {
    return (
      <AuthShell title="Forgot your password?">
        <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
      </AuthShell>
    );
  }
  if (mode === "off") {
    return (
      <AuthShell title="Forgot your password?" subtitle="A link to choose a new password can't be emailed from this server.">
        <div role="status" className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          <p className="font-medium">Email isn't set up for ClientFlow yet.</p>
          <p>Ask your firm's owner for a link to set a new password. The owner can create one with the recovery command described in the setup guide (DEPLOY.md, Password and two-step recovery).</p>
        </div>
        <BackToSignIn />
      </AuthShell>
    );
  }
  if (send.isSuccess) {
    return (
      <AuthShell title="Check your email" subtitle={send.data.message}>
        <p className="text-sm text-muted-foreground">If no email arrives within a few minutes, check your spam folder, or ask for a new link. A new link replaces any earlier one.</p>
        <Button variant="outline" className="mt-5 w-full" onClick={() => send.reset()}>
          Ask for a new link
        </Button>
        <BackToSignIn />
      </AuthShell>
    );
  }
  return (
    <AuthShell title="Forgot your password?" subtitle="Enter the email address you use for ClientFlow. We'll email you a link to choose a new password.">
      {mode === "development_log" && (
        <p role="status" className="mb-4 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
          Development server: email isn't set up, so the link is written to this server's log instead of being emailed.
        </p>
      )}
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          send.mutate();
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="forgot-email">Email</Label>
          <Input id="forgot-email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <FormError error={send.error} />
        <Button type="submit" className="w-full" disabled={send.isPending}>
          Email me a link
        </Button>
        <p className="text-xs text-muted-foreground">Choosing a new password doesn't turn off two-step verification: you'll still enter the code from your authenticator app when you sign in.</p>
      </form>
      <BackToSignIn />
    </AuthShell>
  );
}

/** Opened from an emailed or recovery link (the token is in the address's fragment, never sent to a server log). */
export function ResetPasswordPage() {
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const reset = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", "/api/auth/reset-password", { token, password }),
    onSuccess: () => {
      // Remove the token from the address bar and history.
      window.history.replaceState(null, "", "/reset-password");
    },
  });
  if (reset.isSuccess) {
    return (
      <AuthShell title="Password set" subtitle="Sign in with your email address and your new password.">
        <p className="text-sm text-muted-foreground">
          You have been signed out on every device. If your account uses two-step verification, you'll still be asked for the code from your authenticator app.
        </p>
        <Button className="mt-5 w-full" onClick={() => window.location.assign("/sign-in")}>
          Sign in
        </Button>
      </AuthShell>
    );
  }
  const mismatch = confirm.length > 0 && confirm !== password;
  const invalid = reset.error instanceof ApiError && reset.error.code === "reset_invalid";
  return (
    <AuthShell title="Choose a new password" subtitle="Use at least 12 characters, including a letter and a number.">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!mismatch) reset.mutate();
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="reset-pw">New password</Label>
          <Input id="reset-pw" type="password" autoComplete="new-password" minLength={12} required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="reset-pw2">Confirm password</Label>
          <Input id="reset-pw2" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          {mismatch && <p className="text-xs text-destructive">Passwords do not match</p>}
        </div>
        <FormError error={reset.error} />
        {invalid && (
          <p className="text-sm">
            <a href="/forgot-password" className="font-medium text-primary underline">
              Ask for a new link
            </a>
          </p>
        )}
        <Button type="submit" className="w-full" disabled={!token || reset.isPending || mismatch}>
          Set password
        </Button>
        {!token && <p className="text-sm text-destructive">This link is incomplete. Please open it directly from the email, or ask for a new one.</p>}
      </form>
      <BackToSignIn />
    </AuthShell>
  );
}
